import React, { useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ParsedProduct } from "../types";
import { lightScrollbarStyle } from "../../../../styles/lightScrollbarStyle";
import productUploadedSvg from "../../../../assets/img/productUploaded.svg";

interface SuccessContentProps {
  validCount: number;
  notFoundProducts: ParsedProduct[];
}

const SUCCESS_TEXT = {
  TITLE: "Bulk Products Uploaded",
  PRODUCTS_STATUS: (found: number, uploaded: number) =>
    `${found} products are ready for import (out of ${uploaded} uploaded)`,
  PRODUCTS_NOT_FOUND: (count: number) =>
    `${count} of products mentioned in CSV file were not found in our system.`,
  EXPAND_BUTTON_SHOW: "View",
  EXPAND_BUTTON_HIDE: "Hide",
  TABLE_HEADER_PRODUCT_ID: "Product ID",
  TABLE_HEADER_PRODUCT_NAME: "Product Name",
};

export const SuccessContent: React.FC<SuccessContentProps> = ({
  validCount,
  notFoundProducts,
}) => {
  const [expanded, setExpanded] = useState(false);
  const hasNotFoundProducts = notFoundProducts.length > 0;
  const totalUploaded = validCount + notFoundProducts.length;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  return (
    <Fade in={true} timeout={800}>
      <Stack spacing={2} p={2}>
        <Stack direction="row" alignItems="center">
          <Box marginRight={2}>
            <img
              src={productUploadedSvg}
              alt="List Uploaded"
              width={60}
              height={60}
            />
          </Box>
          <Box height={55}>
            <Typography variant="subtitle1" fontWeight="bold">
              {SUCCESS_TEXT.TITLE}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {SUCCESS_TEXT.PRODUCTS_STATUS(validCount, totalUploaded)}
            </Typography>
          </Box>
        </Stack>

        {hasNotFoundProducts && (
          <>
            <Alert
              severity="info"
              sx={{ alignItems: "center" }}
              action={
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleExpandClick}
                  endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  {expanded
                    ? SUCCESS_TEXT.EXPAND_BUTTON_HIDE
                    : SUCCESS_TEXT.EXPAND_BUTTON_SHOW}
                </Button>
              }
            >
              {SUCCESS_TEXT.PRODUCTS_NOT_FOUND(notFoundProducts.length)}
            </Alert>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <TableContainer
                component={Paper}
                sx={{
                  height: "200px",
                  overflowY: "auto",
                  boxShadow: "none",
                  border: "1px solid #f0f0f0",
                  ...lightScrollbarStyle,
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {SUCCESS_TEXT.TABLE_HEADER_PRODUCT_ID}
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        {SUCCESS_TEXT.TABLE_HEADER_PRODUCT_NAME}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notFoundProducts.map((product, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{product.product_id}</TableCell>
                        <TableCell>{product.product_name || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </>
        )}
      </Stack>
    </Fade>
  );
};
